/**
 * ContractTemplateEditorPage
 *
 * Page for creating and editing contract templates.
 * Includes a rich text editor with merge field support.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  contractTemplatesApi,
  ContractTemplate,
  CustomField,
  MergeField,
} from '../../services/contractsApi';
import { getErrorMessage } from '../../services/api';

// Default template content
const DEFAULT_CONTENT = `<h1>Service Agreement</h1>

<p>This Service Agreement ("Agreement") is entered into as of {{currentDate}} between:</p>

<p><strong>Service Provider:</strong><br>
{{organization.name}}<br>
{{organization.address}}<br>
{{organization.email}}</p>

<p><strong>Client:</strong><br>
{{client.name}}<br>
{{client.address}}<br>
{{client.email}}</p>

<h2>1. Services</h2>
<p>The Service Provider agrees to provide the following services:</p>
<ul>
  <li>[Describe services here]</li>
</ul>

<h2>2. Term</h2>
<p>This Agreement shall commence on {{currentDate}} and continue until terminated by either party with 30 days written notice.</p>

<h2>3. Compensation</h2>
<p>The Client agrees to pay the Service Provider according to the following terms:</p>
<ul>
  <li>[Describe payment terms here]</li>
</ul>

<h2>4. Confidentiality</h2>
<p>Both parties agree to maintain the confidentiality of any proprietary information shared during the course of this Agreement.</p>

<h2>5. Signatures</h2>
<p>By signing below, both parties agree to the terms and conditions outlined in this Agreement.</p>
`;

export default function ContractTemplateEditorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');

  const isEditing = Boolean(id);
  const isDuplicating = Boolean(duplicateId);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [category, setCategory] = useState('');
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  // UI state
  const [saveError, setSaveError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Fetch template if editing
  const {
    data: template,
    isLoading: templateLoading,
    error: templateError,
  } = useQuery({
    queryKey: ['contract-template', id || duplicateId],
    queryFn: () => contractTemplatesApi.get(id || duplicateId!),
    enabled: Boolean(id || duplicateId),
  });

  // Fetch merge fields
  const { data: mergeFieldsData } = useQuery({
    queryKey: ['merge-fields'],
    queryFn: () => contractTemplatesApi.getMergeFields(),
  });

  // Populate form when template loads
  useEffect(() => {
    if (template) {
      setName(isDuplicating ? `${template.name} (Copy)` : template.name);
      setDescription(template.description || '');
      setContent(template.content);
      setCategory(template.category || '');
      setCustomFields(template.customFields || []);
    }
  }, [template, isDuplicating]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: contractTemplatesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-templates'] });
      setSnackbarMessage('Template created successfully');
      setSnackbarOpen(true);
      setTimeout(() => navigate('/contracts/templates'), 1000);
    },
    onError: (error) => {
      setSaveError(getErrorMessage(error));
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, params }: { id: string; params: Partial<ContractTemplate> }) =>
      contractTemplatesApi.update(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-templates'] });
      queryClient.invalidateQueries({ queryKey: ['contract-template', id] });
      setSnackbarMessage('Template saved successfully');
      setSnackbarOpen(true);
    },
    onError: (error) => {
      setSaveError(getErrorMessage(error));
    },
  });

  // Handle save
  const handleSave = () => {
    setSaveError(null);

    if (!name.trim()) {
      setSaveError('Template name is required');
      return;
    }

    if (!content.trim()) {
      setSaveError('Template content is required');
      return;
    }

    const params = {
      name: name.trim(),
      description: description.trim() || undefined,
      content,
      category: category.trim() || undefined,
      customFields,
    };

    if (isEditing && id) {
      updateMutation.mutate({ id, params });
    } else {
      createMutation.mutate(params);
    }
  };

  // Handle merge field copy
  const handleCopyMergeField = (key: string) => {
    navigator.clipboard.writeText(key);
    setSnackbarMessage(`Copied ${key} to clipboard`);
    setSnackbarOpen(true);
  };

  // Handle add custom field
  const handleAddCustomField = () => {
    setCustomFields([
      ...customFields,
      { name: '', label: '', type: 'text', required: false },
    ]);
  };

  // Handle update custom field
  const handleUpdateCustomField = (index: number, field: Partial<CustomField>) => {
    const updated = [...customFields];
    const current = updated[index];
    if (!current) return;
    updated[index] = {
      name: field.name ?? current.name,
      label: field.label ?? current.label,
      type: field.type ?? current.type,
      required: field.required ?? current.required,
      defaultValue: field.defaultValue ?? current.defaultValue,
    };
    setCustomFields(updated);
  };

  // Handle remove custom field
  const handleRemoveCustomField = (index: number) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  // Loading state
  if (templateLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={500} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={500} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Error state
  if (templateError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {getErrorMessage(templateError)}
        </Alert>
      </Box>
    );
  }

  const mergeFields = mergeFieldsData?.byCategory || {};
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/contracts/templates')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            {isEditing ? 'Edit Template' : isDuplicating ? 'Duplicate Template' : 'New Template'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Template'}
        </Button>
      </Box>

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            {/* Template Details */}
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="e.g., Standard Service Agreement"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g., Services"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Brief description of when to use this template"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Content Editor */}
            <Typography variant="h6" gutterBottom>
              Template Content
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use merge fields like {"{{client.name}}"} to automatically fill in data.
              Copy merge fields from the panel on the right.
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={20}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your contract content here..."
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Paper>

          {/* Custom Fields */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Custom Fields
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add fields that users fill in when creating contracts from this template
                </Typography>
              </Box>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddCustomField}
                size="small"
              >
                Add Field
              </Button>
            </Box>

            {customFields.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No custom fields added yet. Click "Add Field" to create one.
              </Typography>
            ) : (
              customFields.map((field, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    mb: 2,
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  <TextField
                    size="small"
                    label="Field Name"
                    value={field.name}
                    onChange={(e) => handleUpdateCustomField(index, { name: e.target.value.replace(/\s/g, '') })}
                    placeholder="projectFee"
                    helperText="No spaces"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Label"
                    value={field.label}
                    onChange={(e) => handleUpdateCustomField(index, { label: e.target.value })}
                    placeholder="Project Fee"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Type"
                    value={field.type}
                    onChange={(e) => handleUpdateCustomField(index, { type: e.target.value as CustomField['type'] })}
                    sx={{ width: 120 }}
                    SelectProps={{ native: true }}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="currency">Currency</option>
                  </TextField>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveCustomField(index)}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))
            )}

            {customFields.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Use custom fields in your template with: {"{{custom.fieldName}}"}
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Sidebar - Merge Fields */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, position: 'sticky', top: 16 }}>
            <Typography variant="h6" gutterBottom>
              Merge Fields
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click to copy a merge field, then paste it in your template.
            </Typography>

            {Object.entries(mergeFields).map(([category, fields]) => (
              <Accordion key={category} defaultExpanded={category === 'client'}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                    {category} Fields
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(fields as MergeField[]).map((field) => (
                      <Tooltip key={field.key} title={`${field.description} (e.g., ${field.example})`}>
                        <Chip
                          label={field.label}
                          size="small"
                          onClick={() => handleCopyMergeField(field.key)}
                          onDelete={() => handleCopyMergeField(field.key)}
                          deleteIcon={<CopyIcon fontSize="small" />}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}

            {customFields.length > 0 && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Your Custom Fields
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {customFields.filter(f => f.name).map((field) => (
                      <Chip
                        key={field.name}
                        label={field.label || field.name}
                        size="small"
                        color="primary"
                        onClick={() => handleCopyMergeField(`{{custom.${field.name}}}`)}
                        onDelete={() => handleCopyMergeField(`{{custom.${field.name}}}`)}
                        deleteIcon={<CopyIcon fontSize="small" />}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
}
